import { useEffect, useRef, useState } from "react";

export function useOnVisibilityChange<E extends HTMLElement>({
  threshold,
  onVisibilityChange,
}: {
  threshold: number;
  onVisibilityChange?: (isVisible: boolean) => void;
}) {
  const ref = useRef<E>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      // We are only ever observing a single entry, so we can safely grab the
      // first entry in the list.
      ([entry]) => {
        if (!entry) {
          return;
        }

        onVisibilityChange?.(entry.isIntersecting);
        console.debug("visibility", entry.isIntersecting);
      },
      { threshold },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [threshold, onVisibilityChange]);

  return ref;
}

export function useObserveVisibility<E extends HTMLElement>({ threshold }: { threshold: number }) {
  const ref = useRef<E>(null);
  const [isFullyVisible, setIsFullyVisible] = useState(false);

  useOnVisibilityChange({ threshold, onVisibilityChange: setIsFullyVisible });

  return [ref, isFullyVisible] as const;
}
